import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, User, Eye, Clock, Share2, Bookmark, Tag } from 'lucide-react';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArticleDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [articleRes, settingsRes] = await Promise.all([
          axios.get(`${API}/cms/article/${slug}`),
          axios.get(`${API}/cms/settings`)
        ]);
        setData(articleRes.data);
        setSettings(settingsRes.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Article not found');
        } else {
          setError('Failed to load article');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const estimateReadTime = (content) => {
    if (!content) return '1 min read';
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min read`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <MegaMenu />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-4xl font-heading font-bold text-white mb-4">{error}</h1>
          <p className="text-white/60 mb-8">The article you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            <ArrowLeft size={18} />
            Back to Insights
          </Link>
        </div>
        <Footer settings={settings} />
      </div>
    );
  }

  const { article, related } = data || {};

  return (
    <div className="min-h-screen bg-background">
      <MegaMenu />
      
      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-b from-secondary/10 to-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Back Link */}
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 text-white/60 hover:text-primary mb-6 transition-colors"
              data-testid="back-to-insights"
            >
              <ArrowLeft size={18} />
              Back to Insights
            </Link>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {article?.category && (
                <span className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
                  {article.category}
                </span>
              )}
              {article?.sport && (
                <span className="px-3 py-1 bg-white/5 text-white/60 text-sm rounded-full">
                  {article.sport}
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6 leading-tight" data-testid="article-title">
              {article?.title}
            </h1>

            {/* Author & Date */}
            <div className="flex flex-wrap items-center gap-6 text-white/60">
              {article?.author && (
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <User size={18} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{article.author}</p>
                    <p className="text-xs text-white/50">Author</p>
                  </div>
                </div>
              )}
              {article?.published_at && (
                <div className="flex items-center gap-2">
                  <Calendar size={16} />
                  <span>{formatDate(article.published_at)}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Clock size={16} />
                <span>{estimateReadTime(article?.content)}</span>
              </div>
              {article?.views > 0 && (
                <div className="flex items-center gap-2">
                  <Eye size={16} />
                  <span>{article.views} views</span>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cover Image */}
      {article?.cover_image && (
        <section className="pb-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <img
              src={article.cover_image}
              alt={article.title}
              className="w-full h-64 sm:h-96 object-cover rounded-2xl"
            />
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-12 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-12">
            {/* Main Content */}
            <article className="lg:col-span-3">
              {/* Excerpt */}
              {article?.excerpt && (
                <p className="text-xl text-white/80 leading-relaxed mb-8 font-medium border-l-4 border-primary pl-6">
                  {article.excerpt}
                </p>
              )}

              {/* Body */}
              <div 
                className="prose prose-invert prose-lg max-w-none"
                data-testid="article-content"
              >
                {article?.content ? (
                  <div className="text-white/80 leading-relaxed whitespace-pre-wrap">
                    {article.content}
                  </div>
                ) : (
                  <p className="text-white/60 italic">Full article content coming soon...</p>
                )}
              </div>

              {/* Tags */}
              {article?.tags && (
                <div className="mt-10 pt-8 border-t border-border">
                  <div className="flex items-center gap-2 mb-4">
                    <Tag size={18} className="text-primary" />
                    <span className="text-white font-medium">Tags</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {article.tags.split(',').map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 bg-white/5 text-white/70 text-sm rounded-full hover:bg-white/10 transition-colors"
                      >
                        {tag.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share */}
              <div className="mt-10 pt-8 border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-white/60">Share this article:</span>
                    <button className="p-2 bg-white/5 rounded-lg text-white/60 hover:text-primary hover:bg-white/10 transition-colors">
                      <Share2 size={18} />
                    </button>
                    <button className="p-2 bg-white/5 rounded-lg text-white/60 hover:text-primary hover:bg-white/10 transition-colors">
                      <Bookmark size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24">
                {/* Author Card */}
                {article?.author && (
                  <div className="p-6 bg-card-bg border border-border rounded-xl mb-6">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                      <span className="text-2xl font-heading font-bold text-primary">
                        {article.author.charAt(0)}
                      </span>
                    </div>
                    <h4 className="text-lg font-heading font-bold text-white">{article.author}</h4>
                    <p className="text-white/50 text-sm mt-1">Sports Writer</p>
                  </div>
                )}

                {/* Newsletter */}
                <div className="p-6 bg-gradient-to-br from-primary/20 to-secondary/20 border border-primary/30 rounded-xl">
                  <h4 className="text-lg font-heading font-bold text-white mb-2">Stay Updated</h4>
                  <p className="text-white/60 text-sm mb-4">Get the latest sports insights delivered to your inbox.</p>
                  <input
                    type="email"
                    placeholder="Your email"
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-white placeholder:text-white/40 text-sm mb-3 focus:outline-none focus:border-primary"
                  />
                  <button className="w-full px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-medium rounded-lg transition-colors">
                    Subscribe
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {related?.length > 0 && (
        <section className="py-16 bg-card-bg border-t border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-heading font-bold text-white mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((item, idx) => (
                <motion.article
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="group bg-background border border-border rounded-xl overflow-hidden hover:border-primary/50 transition-all"
                  data-testid={`related-article-${item.id}`}
                >
                  <div className="h-36 bg-gradient-to-br from-secondary/10 to-primary/10" />
                  <div className="p-5">
                    {item.category && (
                      <span className="text-xs text-primary font-medium">{item.category}</span>
                    )}
                    <h3 className="text-base font-heading font-bold text-white mt-1 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-white/50 text-sm line-clamp-2">{item.excerpt}</p>
                    <Link
                      to={`/insights/${item.slug}`}
                      className="inline-block mt-3 text-primary text-sm hover:underline"
                    >
                      Read more →
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-heading font-bold text-white mb-4">
            Want more insights?
          </h2>
          <p className="text-white/60 mb-6">
            Explore our full collection of sports industry analysis and research.
          </p>
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg transition-colors"
          >
            Browse All Articles
          </Link>
        </div>
      </section>

      <Footer settings={settings} />
    </div>
  );
};

export default ArticleDetailPage;
